<template>
  <div class="asc-parameter-graph">
    <asc-spinner-wrapper :loading="loading">
      <div class="asc-parameter-graph--chart">
        <p v-if="data.length > 0">
          {{ getHistogramTitle }}
          <asc-tooltip
            class="histogramTooltip"
            :tooltip="getHistogramTitleTooltip"
            max-width="600"
            right
          >
            <asc-icon icon="info" :size="16" color="#057874"
          /></asc-tooltip>
        </p>
        <asc-histogram-chart
          v-if="!isError && !isEmpty"
          :options="getChartOptions"
          :thresholds="getThresholds"
        ></asc-histogram-chart>
        <div ref="plotLineTooltip" class="asc-histogram--plotLineTooltip">
          <asc-alert-icon :status="plotLineToolTipIconType"></asc-alert-icon
          >{{ plotLineToolTipText }}
        </div>
        <div v-if="isError" class="asc-histogram--errorMessage">
          <asc-validation-message
            :show="true"
            message="Error loading histogram data"
          />
        </div>
        <div v-if="isEmpty" class="asc-histogram--errorMessage">
          <asc-validation-message
            :show="true"
            message="No histogram data to display. Check your parameter and configurations."
          />
        </div>
      </div>
    </asc-spinner-wrapper>
  </div>
</template>

<script>
import AscAlertIcon from '@/components/atoms/AscAlertIcon'
import AscIcon from '@/components/atoms/AscIcon'
import AscSpinnerWrapper from '@/components/atoms/AscSpinnerWrapper'
import AscTooltip from '@/components/atoms/AscTooltip'
import AscValidationMessage from '@/components/atoms/AscValidationMessage'
import { AscHistogramMixin } from '@/components/mixins'
import variables from '@/styles/_varsJs.scss'
import AscHistogramChart from './AscHistogramChart'

export default {
  name: 'AscHistogram',

  components: {
    AscSpinnerWrapper,
    AscHistogramChart,
    AscAlertIcon,
    AscValidationMessage,
    AscTooltip,
    AscIcon,
  },

  mixins: [AscHistogramMixin],

  props: {
    unitName: {
      type: String,
      default: '',
    },
    data: {
      type: Array,
      default: () => [],
    },
    parameterName: {
      type: String,
      default: undefined,
    },
    minRange: {
      type: [Number, String],
      default: null,
    },
    maxRange: {
      type: [Number, String],
      default: null,
    },
    lowWarning: {
      type: [Number, String],
      default: null,
    },
    lowCaution: {
      type: [Number, String],
      default: null,
    },
    highCaution: {
      type: [Number, String],
      default: null,
    },
    highWarning: {
      type: [Number, String],
      default: null,
    },
    loading: {
      type: Boolean,
      default: true,
    },
    airlineName: {
      type: String,
      default: undefined,
    },
    alertType: {
      type: String,
      default: undefined,
    },
    lowWarningValid: {
      type: Boolean,
      default: true,
    },
    lowCautionValid: {
      type: Boolean,
      default: true,
    },
    highCautionValid: {
      type: Boolean,
      default: true,
    },
    highWarningValid: {
      type: Boolean,
      default: true,
    },
    maxRangeValid: {
      type: Boolean,
      default: true,
    },
  },

  data() {
    return {
      warningPlotBandColor: 'rgba(194,0,0,.1)',
      cautionPlotBandColor: 'rgba(194, 108, 0, 0.1)',
      warningPlotLineColor: 'rgb(194,0,0)',
      cautionPlotLineColor: 'rgb(194,108,0)',
      normalRangePlotBandColor: variables.gray700,
      outOfRangePlotBandColor: variables.gray200,
      plotLineToolTipText: null,
      plotLineToolTipIconType: '',
      minRangeTick: -1,
      maxRangeTick: -1,
      lowWarningTick: -1,
      lowCautionTick: -1,
      highWarningTick: -1,
      highCautionTick: -1,
    }
  },
  computed: {
    getChartOptions() {
      return {
        chart: {
          zoomType: 'xy',
        },
        title: {
          text: undefined,
        },
        legend: {
          enabled: false,
        },
        navigator: {
          enabled: false,
        },
        rangeSelector: {
          enabled: false,
        },
        series: [
          {
            type: 'column',
            name: this.unitName,
            borderColor: variables.primarywhite,
            borderWidth: 1,
            color: variables.gray700,
            data: this.mappedValues,
            xAxis: 0,
            yAxis: 0,
          },
        ],
        xAxis: [
          {
            title: { text: this.unitName },
            categories: this.mappedXAxisTicks,
            zoomEnabled: true,
            crosshair: true,
            labels: {
              format: '{value:.2f}',
            },
            // type: 'category',
            // plotLines: this.calculatePlotLines(),
            // plotBands: this.calculatePlotBands(),
          },
        ],
        yAxis: [
          {
            title: { text: 'Flight Count' },
            alignTicks: false,
            zoomEnabled: true,
            opposite: false,
          },
        ],
        tooltip: {
          formatter: point => {
            const category = point.chart.hoverPoints[0].category
            return `<span style="font-size: 12px; color: #FFFFFF">${point.chart.series[0].name}: ${category}<br/>Count: ${point.chart.hoverPoints[0].options.y}</span>`
          },
          backgroundColor: '#333333',
          padding: 14,
          borderWidth: 0,
        },
      }
    },
    getThresholds() {
      return {
        minRange: this.minRange !== null ? this.minRange : null,
        maxRange: this.maxRange !== null ? this.maxRange : null,
        lowWarning:
          this.lowWarning !== null && this.lowWarningValid
            ? this.lowWarning
            : null,
        lowCaution:
          this.lowCaution !== null && this.lowCautionValid
            ? this.lowCaution
            : null,
        highWarning:
          this.highWarning !== null && this.highWarningValid
            ? this.highWarning
            : null,
        highCaution:
          this.highCaution !== null && this.highCautionValid
            ? this.highCaution
            : null,
      }
    },
    isEmpty() {
      if (!this.loading && this.data.length === 0) {
        return true
      }
      return false
    },
    isError() {
      if (!this.loading && this.data.error) {
        return true
      }
      return false
    },

    getHistogramTitle() {
      return `${this.parameterName} HISTOGRAM FOR ${this.airlineName}`.toUpperCase()
    },

    getHistogramTitleTooltip() {
      if (this.alertType === 'fullflight') {
        return `The histogram contains data from the last 30 days. Each data point is the average value of the selected parameter for a particular flight.`
      } else {
        return `Histogram summarizes data for the Airline's full fleet over the last 100 flights.`
      }
    },

    mappedXAxisTicks() {
      if (this.data.length > 0) {
        // the xAxis ticks are the min value from each bucket
        const newTicks = this.data.map(dataObj => {
          if (this.alertType === 'acmf') {
            return `${+dataObj.min.toFixed(2)}`
          } else {
            return `${+dataObj.value.toFixed(2)}`
          }
        })
        if (this.alertType === 'acmf') {
          newTicks.push(this.data[this.data.length - 1].max.toString())
        }
        return newTicks
      } else {
        return []
      }
    },

    mappedValues() {
      if (this.data?.error) {
        return []
      } else {
        return this.data.map(dataObj => {
          if (this.alertType === 'acmf') {
            return dataObj.bucketCount
          } else {
            return dataObj.flightCount
          }
        })
      }
    },

    minDataValue() {
      return Math.min.apply(null, this.mappedValues)
    },
    maxDataValue() {
      return Math.max.apply(null, this.mappedValues)
    },

    binWidth() {
      return (this.maxDataValue - this.minDataValue) / this.mappedValues.length
    },
  },
}
</script>

<style lang="scss" scoped>
.asc-parameter-graph {
  .asc-histogram--errorMessage {
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    height: 100%;

    .asc-validation-message {
      display: flex;
      align-items: center;
      font-size: 14px;
    }
  }
}

.asc-parameter-graph--chart {
  /* width: 100%; */

  .histogramTooltip {
    display: inline-block;
  }

  .asc-histogram--plotLineTooltip {
    width: 135px;
    height: 40px;
    padding: 8px;
    position: absolute;
    background-color: $gray-200;
    z-index: 9999;
    visibility: hidden;

    .asc-alert-icon--container {
      display: inline-block;
    }
  }

  .highcharts-label-box {
    fill: $gray-900 !important;
  }
}
</style>
